import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/3e77a1c8/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import { StructuredListWrapper, StructuredListHead, StructuredListBody, StructuredListRow, StructuredListInput, StructuredListCell, OrderedList, ListItem } from 'carbon-components-react';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <AnchorLinks mdxType="AnchorLinks">
  <AnchorLink mdxType="AnchorLink">How it works</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Accessibility considerations</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Resources</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Accessibility testing</AnchorLink>
    </AnchorLinks>
    <h2>{`How it works`}</h2>
    <p>{`Notification Carbon components are messages that communicate information to a user. The WAI-ARIA `}<inlineCode parentName="p">{`role="alert"`}</inlineCode>{` is equivalent to `}<inlineCode parentName="p">{`aria-live="assertive"`}</inlineCode>{`. It is used to display a message to the user in a way that attracts the user’s attention without receiving focus to communicate the message without interrupting the users current task. If the notification does not contain an urgent message, consider changing it to `}<inlineCode parentName="p">{`aria-live="polite"`}</inlineCode>{` so the notification is queued until whatever task the user is currently working on is complete.`}</p>
    <p>{`The close button contains an ARIA label of `}<inlineCode parentName="p">{`aria-label="close"`}</inlineCode>{`, and the icon has `}<inlineCode parentName="p">{`aria-hidden="true"`}</inlineCode>{` so it is ignored by assistive technologies. The `}<inlineCode parentName="p">{`Tab`}</inlineCode>{` key is used to move focus to the close button within the notification and the `}<inlineCode parentName="p">{`Space`}</inlineCode>{` key or `}<inlineCode parentName="p">{`Enter`}</inlineCode>{` key activate the button to close the notification.`}</p>
    <h2>{`Accessibility considerations`}</h2>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Avoid using a toast notification for critical alerts or long messages because they are timed and will disappear automatically making it difficult for people with various disabilities to get the entire message. An alert that disappears too quickly can lead to failure of the optional `}<a parentName="p" {...{
            "href": "https://www.w3.org/TR/UNDERSTANDING-WCAG20/time-limits-no-exceptions.html"
          }}>{`WCAG 2.0 success criterion 2.2.3 (AAA)`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Provide a means to turn off nonessential alerts to enhance usability for people with visual and cognitive disabilities. Additional information is available in `}<a parentName="p" {...{
            "href": "https://www.w3.org/TR/UNDERSTANDING-WCAG20/time-limits-postponed.html"
          }}>{`WCAG 2.0 success criterion 2.2.4 (AAA)`}</a>{`. Note: Providing this functionality is optional.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Ensure the use of color and hidden icons are not used as the only means of conveying the importance of the notification.`}</p>
      </li>
    </ol>
    <h2>{`Resources`}</h2>
    <p><strong parentName="p">{`Helpful resources for creating customized accessible implementations`}</strong></p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-practices-1.1/#alert"
        }}>{`W3C W3C WAI-ARIA Authoring Practices Alert Design Pattern`}</a>{` covers the usage of ARIA names, state and roles.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/tutorials/forms/notifications/"
        }}>{`W3C W3C Web Accessibility Tutorial - User Notifications`}</a>{` provides a tutorial on notification accessibility.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.ibm.com/able/guidelines/ci162/accessibility_checklist.html"
        }}>{`IBM Accessibility Checklist Checkpoint`}</a>{`:`}<ul parentName="li">
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.ibm.com/able/guidelines/ci162/error_id.html"
            }}>{`3.3.1 Error Identification`}</a>{` (WCAG Success Criteria `}<a parentName="li" {...{
              "href": "https://www.w3.org/TR/UNDERSTANDING-WCAG20/minimize-error-identified.html"
            }}>{`3.3.1`}</a>{`)`}</li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.ibm.com/able/guidelines/ci162/labels_or_instructions.html"
            }}>{`3.3.2 Labels and Instructions`}</a>{` (WCAG Success Criteria `}<a parentName="li" {...{
              "href": "https://www.w3.org/TR/UNDERSTANDING-WCAG20/minimize-error-cues.html"
            }}>{`3.3.2`}</a>{`)`}</li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.ibm.com/able/guidelines/ci162/error_suggestion.html"
            }}>{`3.3.3 Error Suggestion`}</a>{` (WCAG Success Criteria `}<a parentName="li" {...{
              "href": "https://www.w3.org/TR/UNDERSTANDING-WCAG20/minimize-error-suggestions.html"
            }}>{`3.3.3`}</a>{`)`}</li>
        </ul></li>
    </ul>
    <h2>{`Accessibility testing`}</h2>
    <p>{`Accessibility issues are tracked in the `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon/milestones"
      }}>{`Carbon Component GitHub repository`}</a>{`.`}</p>
    <h3>{`Automated test`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListHead mdxType="StructuredListHead">
        <StructuredListRow head mdxType="StructuredListRow">
          <StructuredListCell head mdxType="StructuredListCell">
            Automated test environment
          </StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Results</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - macOS Mojave version 10.14.6 with VoiceOver
            <br />
            - Chrome version 77.0.3865.90
            <br />
            - Dynamic Assessment Plugin version 1.8.0.0 - IBM Accessibility WCAG
            2.1 Sept. 2019 Ruleset
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>DAP Test</strong>
            <br />- Violations
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3>{`macOS screen reader tests`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListHead mdxType="StructuredListHead">
        <StructuredListRow head mdxType="StructuredListRow">
          <StructuredListCell head mdxType="StructuredListCell">Environment </StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Results</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - macOS Mojave version 10.14.6 with VoiceOver
            <br />
            - Chrome version 77.0.3865.90
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>VoiceOver(VO) test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Press Control-Option-Shift keys to enter the Web content.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press the Tab key. VO announces the notification text and the
                close button.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press the Space key or Enter key to close the notification.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - macOS Mojave version 10.14.6 with VoiceOver
            <br />
            - Safari version 13.0.2
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>VoiceOver(VO) test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Press Control-Option-Shift keys to enter the Web content.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press the Tab key. VO announces the notification text and the
                close button.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press the Space key or Enter key to close the notification.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3>{`Windows screen reader tests`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListHead mdxType="StructuredListHead">
        <StructuredListRow head mdxType="StructuredListRow">
          <StructuredListCell head mdxType="StructuredListCell">Environment</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Results</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - Microsoft Windows 10
            <br />
            - Firefox version 68
            <br />
            - JAWS version 18
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>JAWS test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Press H, JAWS announce the notification title.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press Alt-Down Arrow, JAWS announces each line of the
                notification text.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press the Tab, JAWS announces the close button.
              </ListItem>
              <ListItem mdxType="ListItem">
                Press the Space key or Enter key to close the notification.
              </ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3>{`iOS screen reader tests`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListRow head mdxType="StructuredListRow">
        <StructuredListCell head mdxType="StructuredListCell">Environment </StructuredListCell>
        <StructuredListCell head mdxType="StructuredListCell">Results</StructuredListCell>
      </StructuredListRow>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            - iOS version 13.1.3 with VoiceOver
            <br />
            - Safari version 13.1.3
            <br />- Carbon React version 7.7.1
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <strong>VoiceOver test:</strong>
            <OrderedList mdxType="OrderedList">
              <ListItem mdxType="ListItem">
                Swipe right to the notification and to read the notification
                text.
              </ListItem>
              <ListItem mdxType="ListItem">
                Swipe right to the X icon. VO announces the close button.
              </ListItem>
              <ListItem mdxType="ListItem">Double tap to close the notification.</ListItem>
            </OrderedList>
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      